export enum UserType {
  SysAdmin = "systemadmin",
  // Additive: a Manager's `roles` array is always ["admin", "manager"], never
  // ["manager"] alone. Mirrors spott-server's src/types/enum.ts; values must
  // stay identical across both repos.
  Manager = "manager",
  Admin = "admin",
  Staff = "staff",
  Instructor = "instructor",
  Customer = "customer",
}

// Roles that use the structured first_name/middle_name/last_name split
// (first required, middle/last optional) instead of a single free-text
// full_name. Admin and Manager joined when registerAdmin became phone-first
// and started collecting the parts, so every account a studio creates is named
// the same way; existing admin-tier rows were seeded first_name = full_name by
// the server's credential_security migration. SysAdmin stays out: those are
// Spott's own accounts, created directly and never through the modal.
// Mirrors spott-server's src/types/enum.ts NAME_SPLIT_TYPES.
export const NAME_SPLIT_TYPES: UserType[] = [
  UserType.Customer,
  UserType.Staff,
  UserType.Instructor,
  UserType.Admin,
  UserType.Manager,
];

// Mirrors spott-server's src/types/enum.ts EMPLOYEE_ROLES / KARYAWAN_STAFF_TYPE /
// LOGIN_ROLES exactly. The studio's employees for the Karyawan page and
// payroll: teaching staff (instructor) and non-teaching staff (staff).
// Admin/Manager accounts are dashboard accounts, not payees, unless they also
// hold one of these.
export const EMPLOYEE_ROLES: UserType[] = [UserType.Instructor, UserType.Staff];

// GET /v1/staff/:staff_type value that lists every employee at once (the
// Karyawan page). Every other list keeps asking for `instructor`, so the
// schedule pickers never see a non-teaching Staf.
export const KARYAWAN_STAFF_TYPE = "karyawan";
export type StaffListType = UserType | typeof KARYAWAN_STAFF_TYPE;

// Roles that sign in somewhere (the phone is their identity key).
export const LOGIN_ROLES: UserType[] = [
  UserType.SysAdmin,
  UserType.Manager,
  UserType.Admin,
  UserType.Instructor,
  UserType.Customer,
];

// The one employee type a person displays as on the Karyawan page and the
// payroll rosters: anyone who teaches is an Instruktur, everyone else on the
// employee list is a Staf.
export const karyawanTypeOf = (roles: string[] | undefined): UserType.Instructor | UserType.Staff =>
  roles?.includes(UserType.Instructor) ? UserType.Instructor : UserType.Staff;

// May this person have NO phone number? Only a non-teaching Staf: holds the
// `staff` role and none of LOGIN_ROLES. Same predicate as the server's
// phoneOptionalForRoles; the profile form and the add modal read it so the
// admin never offers what the server refuses.
export const phoneOptionalForRoles = (roles: string[] | undefined): boolean =>
  !!roles?.includes(UserType.Staff) && !roles.some((r) => LOGIN_ROLES.includes(r as UserType));

export enum BusinessType {
  Fitness = "fitness",
  Beauty = "beauty",
  Wellness = "wellness",
}

export enum ClassCategory {
  Private = "private",
  Open = "open",
}

// Which class types the server's auto-complete sweep covers for the
// business while auto_complete_bookings is on. Mirrors AutoCompleteScope in
// spott-server/src/types/enum.ts; keep the values identical.
export enum AutoCompleteScope {
  All = "all",
  Open = "open",
  Private = "private",
}

// Pengaturan -> Masa Berlaku Produk: which products are sold with validity
// starting at the client's first class (business.validity_start_scope, only
// read while validity_start_first_use is on). Mirrors ValidityStartScope in
// spott-server/src/types/enum.ts; keep the values identical.
export enum ValidityStartScope {
  All = "all",
  Membership = "membership",
  Package = "package",
}

// customer_package.validity_start: the rule a sold package snapshot at sale.
// A "first_use" row whose activated_at is still null has not run yet; its
// expiry_date is the activation deadline. Mirrors spott-server.
export enum ValidityStart {
  Purchase = "purchase",
  FirstUse = "first_use",
}

// customer_package_expiry_date_log.reason for system-written rows (actor
// null); a manual admin edit has no reason. Mirrors spott-server.
export enum ExpiryLogReason {
  Activated = "activated",
  ReAnchored = "re_anchored",
  Deactivated = "deactivated",
  ManualActivation = "manual_activation",
  // Extend on a window conflict: the end date kept and locked by an admin.
  Locked = "locked",
}

// customer_package_credit_log.reason. Null = a plain manual admin credit edit.
// Mirrors spott-server src/types/enum.ts (CreditLogReason).
export enum CreditLogReason {
  // The Klien CSV importer's below-total starting balance (credits used on
  // the studio's previous platform).
  Import = "import",
  // A credit returned (or taken back) for a class booked on the package this
  // one was upgraded from.
  UpgradedPackageBooking = "upgraded_package_booking",
  // A class from that package put back to Confirmed by an undo: the booking
  // moved to this package for good, with its credit.
  UpgradedPackageBookingMoved = "upgraded_package_booking_moved",
  // The new package of an upgrade starting below its total by the credits
  // already used on the package it replaced.
  UpgradeCarryOver = "upgrade_carry_over",
  // Cancel Upgrade, the two ends of putting a pair back: the old package
  // gets its credits again, the new package is emptied.
  UpgradeCancelledReturned = "upgrade_cancelled_returned",
  UpgradeCancelledRemoved = "upgrade_cancelled_removed",
  // The package an upgrade replaced, zeroed when its credits moved over.
  UpgradedAway = "upgraded_away",
}

// IMPORTANT: Cancelled and NoShow deliberately store the LEGACY database
// strings ("refund" / "cancelled_without_refund"), NOT "cancelled" / "no_show".
// The status rename is a UI/label-only change (see the status-label map): the
// readable member names changed but the stored values are kept as-is so no data
// migration is needed. Do NOT "fix" these values to match the member names —
// that would orphan every existing row. All logic references the members
// (BookingStatus.Cancelled etc.), so it is unaffected by the underlying string.
export enum BookingStatus {
  // Client-initiated private class request awaiting admin/instructor decision.
  // Pending -> Confirmed (accept) and Pending -> Cancelled (reject) are both
  // irreversible (no undo path). New value — no existing rows, no migration.
  Pending = "pending",
  Confirmed = "confirmed",
  Completed = "completed",
  // The single "cancelled / refunded" outcome; the credit held is returned.
  // Stored as the legacy "refund" string (see note above) — labelled "Cancelled".
  Cancelled = "refund",
  // Customer did not show up: the session credit is consumed, no refund. Stored
  // as the legacy "cancelled_without_refund" string — labelled "No Show".
  NoShow = "cancelled_without_refund",
  // Attended AND credit returned (goodwill refund after the class happened,
  // e.g. unsatisfactory service). Fills the fourth cell of the attended x
  // credit-kept matrix: Completed (yes/kept), NoShow (no/kept), Cancelled
  // (no/returned), Refunded (yes/returned). Descends only from Completed.
  // KEEPS its seat (SEAT_CONSUMING_STATUSES): the refund returns the credit
  // but never resells the slot; only Cancelled frees a seat for replacement.
  // New string value, no legacy DB constraint (unlike Cancelled/NoShow above).
  Refunded = "refunded",
  // Waitlist statuses — preserved from prod's waitlist feature.
  Waitlist = "waitlist",
  WaitlistExpired = "waitlist_expired",
}

// Time-driven schedule status, computed on the server from start/end vs now and
// fully decoupled from booking status. Mirrors the backend ScheduleTimeStatus.
export enum ScheduleTimeStatus {
  Upcoming = "upcoming",
  Ongoing = "ongoing",
  Finished = "finished",
}

export enum Gender {
  Female = "female",
  Male = "male",
}

// Sunday = 0, Saturday = 6 — matches the server's working_hour.day and
// recurring_schedule.local_day_of_the_week convention. Keep in sync.
export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export enum StaffStatus {
  Active = "active",
  OnLeave = "onleave",
}

// Only two real stored states: a leave is either active (Approved) or
// explicitly cancelled. "Mendatang" vs "Selesai" is derived from end_date vs
// now at display time (mirrors ScheduleStatusBadge's time-derived statuses),
// not stored.
export enum LeaveStatus {
  Approved = "approved",
  Cancelled = "cancelled",
}

// ---- Package-products catalog (package_product / packages tiers) ----
// Mirrors spott-server src/types/enum.ts — keep the files in sync.

export enum ProductType {
  // >=1 limited tier, none unlimited. May be a promo (is_promo + window).
  Credit = "credit",
  // Exactly 1 unlimited tier + a required AccessScope.
  Membership = "membership",
}

export enum AccessScope {
  // Membership covers the classes explicitly linked on its tier.
  Selected = "selected",
  // Membership covers ALL live open classes; materialized into the tier's
  // class_type links (synced on product save and on class_type create).
  AllOpen = "all_open",
}

// Whether a product is listed in the customer app's catalogue. Storefront
// only: never gates admin selling, upgrades, analytics or reward scopes.
// Mirrors spott-server src/types/enum.ts.
export enum CatalogVisibility {
  Listed = "listed",
  Hidden = "hidden",
}

export enum ExpiryUnit {
  Day = "day",
  Month = "month",
  Year = "year",
}

// Unit of a studio cutoff ("N hours / minutes before class start"): the
// waitlist auto-promote cutoff and the cancellation cutoff. Mirror of the
// server's CutoffUnit (types/enum.ts); converted to minutes by
// shared/utils/cutoff.ts and nowhere else.
export enum CutoffUnit {
  Hour = "hour",
  Minute = "minute",
}

// ---- Payroll ----
// Mirrors spott-server src/types/enum.ts — keep the files in sync.

export enum PayrollFrequency {
  Weekly = "weekly",
  TwiceMonthly = "twice_monthly",
  Monthly = "monthly",
}

export enum CommissionMode {
  PerClass = "per_class",
  PerAttendee = "per_attendee",
}

export enum CommissionRateStructure {
  Flat = "flat",
  Tiered = "tiered",
  // Percentage of each booking's imputed revenue, encoded x100 on `amount`
  // (e.g. 225 = 2.25%) — see the matching server-side comment in
  // schema.prisma / spott-server src/types/enum.ts. Never valid for
  // CommissionMode.PerClass.
  RevenuePercent = "revenue_percent",
  // Sibling of RevenuePercent, same "Per Pendapatan" tab — pays a flat
  // amount banded by the occurrence's revenue instead of a percentage, via
  // the same shared class-level tier bands Tiered uses (bucketed by revenue
  // Rupiah instead of attendee count). Never valid for CommissionMode.PerClass.
  RevenueTiered = "revenue_tiered",
  // Sibling of RevenueTiered, same revenue-bucketing — but each band's
  // amount is a percentage (x100-scaled, same encoding as RevenuePercent)
  // applied to that occurrence's revenue, instead of a flat Rp payout. The
  // rate itself climbs by band rather than the payout being flat per band.
  // Never valid for CommissionMode.PerClass.
  RevenuePercentTiered = "revenue_percent_tiered",
}

export enum PayrollItemKind {
  Allowance = "allowance",
  Deduction = "deduction",
}

export enum PayrollItemSource {
  Recurring = "recurring",
  Adjustment = "adjustment",
}

export enum PayrollRunStatus {
  Draft = "draft",
  Finalized = "finalized",
  Reopened = "reopened",
}

// weekly_closing_day is a plain 0-6 int matching dayjs's native .day()
// (0=Sunday..6=Saturday) — the spott-server period math (src/services/
// payroll/period.ts) compares against dayjs's .day() directly. Do NOT reuse
// the DayOfWeek enum above for this: DayOfWeek is 1=Monday..7=Sunday, which
// silently agrees with dayjs for Monday-Saturday (1-6) but disagrees on
// Sunday (7 vs 0) — picking "Sunday" as a payroll closing day with DayOfWeek
// values would send 7, which dayjs's .day() can never equal, so the server
// would never recognize any real Sunday as a valid closing date.
// The labels here are fallbacks; render sites map them through
// translate(`payrollPage.weekday.${value}`) so the names follow the language.
export const PAYROLL_WEEKDAY_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Minggu" },
  { value: 1, label: "Senin" },
  { value: 2, label: "Selasa" },
  { value: 3, label: "Rabu" },
  { value: 4, label: "Kamis" },
  { value: 5, label: "Jumat" },
  { value: 6, label: "Sabtu" },
];

// ---- Loyalty stamps + reward vouchers ----
// Mirrors spott-server src/types/enum.ts — keep the files in sync.

// Program lifecycle. Off (default) = not offered; deactivation ERASES open
// cards and resets config.
// Issued vouchers stay valid in every status.
export enum LoyaltyProgramStatus {
  Active = "active",
  Off = "off",
}

// Which credit sources earn stamps. Packages = limited tiers, Memberships =
// unlimited tiers (keyed on the tier's own `unlimited` flag, so ungrouped
// legacy tiers still classify). Reward credits never earn stamps regardless.
export enum LoyaltyProductScope {
  All = "all",
  Packages = "packages",
  Memberships = "memberships",
  Products = "products",
}

// Doubles as voucher.type: a voucher IS a frozen reward.
export enum LoyaltyRewardType {
  FreeCredit = "free_credit",
  Discount = "discount",
}

// Which classes a free-credit reward can book (rewards may also target all
// PRIVATE classes, unlike the membership AccessScope).
export enum LoyaltyClassScope {
  All = "all",
  Selected = "selected",
  AllOpen = "all_open",
  AllPrivate = "all_private",
}

export enum LoyaltyDiscountMode {
  Percent = "percent",
  Fixed = "fixed",
}

// What a discount reward applies to: the whole live catalog, every credit
// package tier, every membership tier, or an explicit list of catalog
// PRODUCTS (credit and membership alike — never individual tiers; the tier
// list is resolved live at redeem time).
export enum LoyaltyDiscountScope {
  All = "all",
  Packages = "packages",
  Memberships = "memberships",
  Products = "products",
}

// WHO may redeem a discount code, separate from LoyaltyDiscountScope (WHAT
// it applies to). Exclusive, not combinable.
export enum DiscountCodeEligibility {
  All = "all",
  NewCustomer = "new_customer",
  ReturningCustomer = "returning_customer",
  NoActivePackage = "no_active_package",
  HasActivePackage = "has_active_package",
  SpecificCustomers = "specific_customers",
}

// STORED voucher states; "expired" is DERIVED server-side and arrives only in
// the `status` field of voucher DTOs.
export enum VoucherStatus {
  Issued = "issued",
  Redeemed = "redeemed",
  Voided = "voided",
}

// packages.system_kind values. Null = a normal, admin-managed package.
export enum PackageSystemKind {
  LoyaltyReward = "loyalty_reward",
  ReferralReward = "referral_reward",
}

// ---- Referral program ----
// Mirrors spott-server src/types/enum.ts — keep the files in sync.

// Program lifecycle. Unlike loyalty, deactivation KEEPS the config and only
// blocks NEW attributions: pending referrals still convert and pay out.
export enum ReferralProgramStatus {
  Active = "active",
  Off = "off",
}

// Which side of a referral a configured reward belongs to.
export enum ReferralSide {
  Referrer = "referrer",
  Friend = "friend",
}

// Window the referrer-reward cap counts over: Jakarta calendar day/month/
// year, or forever (an all-time total).
export enum ReferralCapWindow {
  Day = "day",
  Month = "month",
  Year = "year",
  Forever = "forever",
}

// STORED referral states; "lapsed" is DERIVED server-side and arrives only in
// the `status` field of referral DTOs.
export enum ReferralStatus {
  Pending = "pending",
  Converted = "converted",
}

export type ReferralEffectiveStatus = ReferralStatus | "lapsed";

// Where a purchase's discount came from: a discount code, a loyalty reward or
// a referral reward. Only one discount ever applies to a purchase. Derived by
// the server (util/purchase-discount.ts), never stored. Mirrored from the
// server's types/enum.ts — keep both in sync.
export enum DiscountSource {
  Code = "code",
  Loyalty = "loyalty",
  Referral = "referral",
}

// business.tax_mode. 'inclusive' = the price shown/charged never changes;
// tax is a notional breakdown reported only on the Monthly Statement of
// Account. 'additive' = tax is added on top of the price at purchase time,
// so it's also visible on the Invoice/Receipt PDF. Mirrored from the
// server's types/enum.ts — keep both in sync.
export enum TaxMode {
  Inclusive = "inclusive",
  Additive = "additive",
}

// Why a sale was VOIDED ("Void" / "Void Transaksi"). A voided sale is kept, marked
// and dropped from every revenue figure; it is never deleted. Mirrored from
// the server's types/enum.ts. Keep both in sync.
export enum VoidReason {
  WrongClient = "wrong_client",
  WrongPackage = "wrong_package",
  Duplicate = "duplicate",
  Other = "other",
}
